import { Autocomplete, Stack, TextField, Typography } from '@mui/material';
import React, { useState } from 'react';
import { useDebounce } from 'src/hooks/useDebounce';
import { translateKey } from 'src/locales/translate';
import { WarehouseProductDto } from 'woofwoof-api';
import {
  ClinicalProductKind,
  useStockProduct,
  useStockProductOptions,
} from '../hooks/useClinicalStockProducts';
import { formatProductStock } from '../utils/clinicalStock';
import { preventEnterSubmit } from '../utils/forms';

interface StockProductPickerProps {
  kind: ClinicalProductKind;
  value: string;
  onChange: (product: WarehouseProductDto | null) => void;
  label: string;
  disabled?: boolean;
  error?: string;
  testId: string;
}

const SEARCH_DEBOUNCE_MS = 300;

/**
 * Picks an active Product of the Warehouse, showing its Stock Level (the
 * AssociatedMedicinesEditor search: debounced query, a second line per
 * option). The picked Product stays listed while a search does not return
 * it, so the field never goes blank.
 */
export default function StockProductPicker({
  kind,
  value,
  onChange,
  label,
  disabled = false,
  error,
  testId,
}: StockProductPickerProps): React.ReactElement {
  const [input, setInput] = useState('');
  const search = useDebounce(input, SEARCH_DEBOUNCE_MS);
  const { options, isLoading } = useStockProductOptions(
    kind,
    search,
    !disabled,
  );
  const stored = useStockProduct(value);
  const selected =
    options.find((option) => option.id === value) ?? stored ?? null;
  // Until the debounced search catches up with the typing, the last answer
  // is for other text: offering it would let Enter pick a stale Product.
  const pending = search !== input;
  const listed = pending ? [] : options;
  const shown =
    selected && !listed.some((option) => option.id === selected.id)
      ? [selected, ...listed]
      : listed;

  return (
    <Autocomplete<WarehouseProductDto, false, false, false>
      autoHighlight
      options={shown}
      value={selected}
      loading={isLoading || pending}
      disabled={disabled}
      filterOptions={(all) => all}
      onInputChange={(_event, next, reason) => {
        if (reason === 'input') setInput(next);
      }}
      onChange={(_event, product) => onChange(product)}
      getOptionLabel={(product) => product.name}
      isOptionEqualToValue={(option, current) => option.id === current.id}
      renderOption={(props, product) => (
        <li {...props} key={product.id}>
          <Stack>
            <Typography variant="body2">{product.name}</Typography>
            <Typography
              variant="caption"
              color={product.onHandMilli < 0 ? 'error' : 'text.secondary'}
            >
              {translateKey('warehouse.clinical.onHand', {
                // Phase 1's Stock Level text: "3 op. + 2 ampułki".
                quantity: formatProductStock(product.onHandMilli, product),
              })}
            </Typography>
          </Stack>
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          error={Boolean(error)}
          helperText={error}
          onKeyDown={preventEnterSubmit}
          inputProps={{ ...params.inputProps, 'data-testid': testId }}
        />
      )}
    />
  );
}
